import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import React from 'react';
import Card from '../components/card';
import { InfoCopy, InfoLink } from '../components/info-link';
import { lightGray } from '../shared/theme';

/**
 * Using a custom wrapper in lieu of the one that already exists because
 * the design is unique to this card. (Last option offset by a thin solid line.)
 * If this design ends up being reused, it can be elevated.
 */
const RadioButtonsGroup = ({ onChange, value, name, choices }) => {
  const radioChoices = [...choices];
  const lastOption = radioChoices.pop();

  return (
    <RadioButtonGroup
      name={name}
      style={{ margin: '1em 0' }}
      onChange={(event) => onChange(event.target.value)}
      valueSelected={value}
    >
      {radioChoices.map(({ value, label }) => (
        <RadioButton key={value} value={value} label={label} style={{ marginTop: '1em' }} />
      ))}

      <RadioButton
        key={lastOption.value}
        value={lastOption.value}
        label={lastOption.label}
        style={{
          borderTopColor: lightGray,
          borderTopStyle: 'solid',
          borderTopWidth: '2px',
          marginTop: '0.5em'
        }}
        labelStyle={{
          marginTop: '0.5em'
        }}
        iconStyle={{
          marginTop: '0.2em'
        }}
      />
    </RadioButtonGroup>
  );
};

const Info = (props) => (
  <InfoLink title="Veteran's Benefits" {...props}>
    <InfoCopy>
      The U.S. Department of Veterans Affairs (VA) offers service members, veterans, and eligible
      surviving spouses home loan benefits and housing related programs to help buy, build, repair,
      retain, or adapt a home for personal occupancy.
      <br />
      <br />
      To learn more about program benefits and specific eligibility requirements, visit the VA{' '}
      <a
        href="https://www.benefits.va.gov/homeloans/"
        className="link__hover--yellow"
        target="_blank"
        rel="noreferrer"
      >
        website
      </a>
      .
    </InfoCopy>
  </InfoLink>
);

const choices = [
  {
    value: 'Eligible',
    label: 'Yes, I am an active-duty service member or retired veteran.'
  },
  {
    value: 'SurvivingSpouse',
    label: 'Yes, I am a surviving spouse of someone who served.'
  },
  {
    value: 'Coborrower',
    label: 'Yes, my co-borrower qualifies as one of the above.'
  },
  {
    value: 'Ineligible',
    label: 'No, I am not eligible.'
  }
];

/**
 * Determine the state for the card based on the given model.
 *
 * @param {*} model Current questionnaire model
 */
const determineState = ({ model }) => {
  // The relationship between the model and the radio buttons is more
  // complicated in this card. This assignment ensures that the right
  // option is shown as selected.
  return {
    value:
      model.getIn(['coborrower', 'vaQualification']) === 'Eligible'
        ? 'Coborrower'
        : model.getIn(['borrower', 'vaQualification'])
        ? model.getIn(['borrower', 'vaQualification'])
        : null
  };
};

/**
 * The VeteransBenefits card.
 *
 * This card touches multiple models, so it eschews the Card Lenses.
 * Therefore, it must set the complete prop, which is normally automatically
 *   populated by a Card Lens.
 */
class VeteransBenefits extends React.Component {
  constructor(props) {
    super(props);
    this.state = determineState(props);

    const updateBorrower = props.onChange.bind(null, 'borrower', 'vaQualification');
    const updateCoborrower = props.onChange.bind(null, 'coborrower', 'vaQualification');
    const updateLoan = props.onChange.bind(null, 'loan', 'hasCoborrower');

    /**
     * @param val One of the options for the veterans' benefits radio buttons:
     *             - "Eligible"
     *             - "SurvivingSpouse"
     *             - "Ineligible"
     */
    this.onChange = (val) => {
      this.setState({ value: val });
      // TODO: The backend currently does not support settting fields
      //   to null because it cannot differentiate a null fields from
      //   a field that just isn't set. As such, setting a field to
      //   null will immediately re-populate the field with its
      //   previous value.
      //
      // We have to force the fields to a non-null values which is why
      //   we're setting borrower/coborrower to ineligible instead of
      //   nulling them out.
      //
      // Per Product Owner's mediation, it's okay to set the coborrower
      //   field to true and never unset it, but it's not okay to set
      //   it to false because it's easier to notice the coborrower
      //   stuff was added incorrectly than it is to notice it never
      //   asked for your coborrower.
      if (val === 'Coborrower') {
        updateBorrower('Ineligible');
        updateCoborrower('Eligible');
        updateLoan(true);
      } else if (val === 'Eligible') {
        updateBorrower('Eligible');
        updateCoborrower('Ineligible');
        updateLoan(null);
      } else if (val === 'SurvivingSpouse') {
        updateBorrower('SurvivingSpouse');
        updateCoborrower('Ineligible');
        updateLoan(null);
      } else if (val === 'Ineligible') {
        updateBorrower('Ineligible');
        updateCoborrower('Ineligible');
        updateLoan(null);
      }
    };
  }

  componentWillReceiveProps(props) {
    this.setState(determineState(props));
  }

  render() {
    const { model, onChange: _, ...cardProps } = this.props;
    const { value } = this.state;

    return (
      <Card
        title="Are you eligible for veteran's benefits?"
        info={Info}
        {...cardProps}
        complete={!!VeteransBenefits.completed({ model })}
      >
        <RadioButtonsGroup
          name="vaQualification"
          value={value}
          onChange={this.onChange}
          choices={choices}
        />
      </Card>
    );
  }
}

VeteransBenefits.id = 'VeteransBenefits';

VeteransBenefits.completed = ({ model }) =>
  model.getIn(['borrower', 'vaQualification']) || model.getIn(['coborrower', 'vaQualification']);

export { VeteransBenefits };



// WEBPACK FOOTER //
// ./src/cards/veterans-benefits.js